/**
 * Trampoline: solid rectangle that launches the player on contact in a
 * configured direction. Default direction is 'up' (floor pad). Use 'down'
 * for ceiling pads, 'left'/'right' for walls. Combine floor + ceiling pads
 * to make bouncy tunnels.
 */

import Matter from 'matter-js';
import { Container, Graphics } from 'pixi.js';

export type TrampolineDirection = 'up' | 'down' | 'left' | 'right';

export interface TrampolineOptions {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Kick speed in px/step (default 16). */
  power?: number;
  /** Direction of launch. Default 'up'. */
  direction?: TrampolineDirection;
}

export class Trampoline {
  readonly body: Matter.Body;
  /** Outer container, positioned at world centre — so the inner pad can
   *  squash/scale around its centre and a fade-out ring can be drawn around
   *  the pad on impact. */
  readonly view: Container;
  readonly power: number;
  readonly direction: TrampolineDirection;

  /** Local launch axis unit vector — direction the launch points. */
  private readonly axis: { x: number; y: number };
  /** Pad child whose scale is animated (squash on hit, springs back). */
  private readonly pad: Graphics;
  /** Impact ring (drawn in front of pad, fades + grows). */
  private readonly ring: Graphics;
  /** Animation state. */
  private padScalePerp = 1;   // along launch axis
  private padScaleAlong = 1;  // perpendicular to launch axis
  private padVelPerp = 0;
  private padVelAlong = 0;
  private ringT = 1; // 0..1, 1 = fully faded
  private ringIntensity = 0;
  private readonly halfW: number;
  private readonly halfH: number;

  constructor(opts: TrampolineOptions) {
    this.power = opts.power ?? 16;
    this.direction = opts.direction ?? 'up';
    this.body = Matter.Bodies.rectangle(
      opts.x + opts.w / 2,
      opts.y + opts.h / 2,
      opts.w,
      opts.h,
      {
        isStatic: true,
        friction: 0.05,
        frictionStatic: 0.05,
        restitution: 0.4,
        label: 'trampoline',
      },
    );

    let dx = 0, dy = -1;
    if (this.direction === 'down')  { dx = 0; dy = 1; }
    if (this.direction === 'left')  { dx = -1; dy = 0; }
    if (this.direction === 'right') { dx = 1; dy = 0; }
    this.axis = { x: dx, y: dy };

    // Color tiered by power so players can read strength at a glance.
    const tier =
      this.power <= 14 ? { fill: 0x33cc66, stroke: 0x0a3a18 } :
      this.power <= 20 ? { fill: 0xff2e88, stroke: 0x4a0a2a } :
      this.power <= 28 ? { fill: 0xff8a1f, stroke: 0x5a2a05 } :
                         { fill: 0x9b3cff, stroke: 0x2a0a4a };

    this.halfW = opts.w / 2;
    this.halfH = opts.h / 2;

    // Outer container at world centre.
    this.view = new Container();
    this.view.x = opts.x + this.halfW;
    this.view.y = opts.y + this.halfH;

    // Impact ring (drawn behind pad).
    this.ring = new Graphics();
    this.view.addChild(this.ring);

    // Pad drawn around local origin so scale anchors on its centre.
    this.pad = new Graphics();
    const x0 = -this.halfW, y0 = -this.halfH;
    this.pad.roundRect(x0, y0, opts.w, opts.h, Math.min(Math.min(opts.w, opts.h) / 2, 14))
      .fill(tier.fill)
      .stroke({ color: tier.stroke, width: 2 });
    const stripe = 12;
    for (let i = 0; i < opts.w + opts.h; i += stripe * 2) {
      this.pad.moveTo(x0 + i, y0)
        .lineTo(x0 + i - opts.h, y0 + opts.h)
        .stroke({ color: 0xffffff, width: 4, alpha: 0.35 });
    }
    const len = Math.min(opts.w, opts.h) * 0.35;
    const tipX = dx * len, tipY = dy * len;
    const tailX = -dx * len, tailY = -dy * len;
    this.pad.moveTo(tailX, tailY).lineTo(tipX, tipY).stroke({ color: 0xffffff, width: 3, alpha: 0.85 });
    const head = 6;
    const ang = Math.atan2(dy, dx);
    this.pad.moveTo(tipX, tipY)
      .lineTo(tipX - Math.cos(ang - 0.5) * head, tipY - Math.sin(ang - 0.5) * head)
      .moveTo(tipX, tipY)
      .lineTo(tipX - Math.cos(ang + 0.5) * head, tipY - Math.sin(ang + 0.5) * head)
      .stroke({ color: 0xffffff, width: 3, alpha: 0.85 });
    this.view.addChild(this.pad);
  }

  /** Trigger a squash-and-spring impact reaction.
   *  `intensity` ∈ [0,1] scales the squash depth and ring brightness. */
  bounce(intensity: number): void {
    const k = Math.max(0.2, Math.min(1.5, intensity));
    // Push the pad inward along the launch axis (squash thickness) and
    // outward perpendicular to it (bulge width). Velocities, not absolute
    // positions, so back-to-back hits compound.
    this.padVelPerp  -= 0.55 * k;   // shrink along axis (thickness)
    this.padVelAlong += 0.30 * k;   // expand across axis (width)
    // Restart the ring effect.
    this.ringT = 0;
    this.ringIntensity = k;
  }

  /** Animate pad squash and ring fade. dt in seconds. */
  update(dt: number): void {
    // Spring back to (1,1) — critically-damped feel.
    const k = 90;          // stiffness
    const d = 14;          // damping
    const aPerp  = (1 - this.padScalePerp)  * k - this.padVelPerp  * d;
    const aAlong = (1 - this.padScaleAlong) * k - this.padVelAlong * d;
    this.padVelPerp  += aPerp  * dt;
    this.padVelAlong += aAlong * dt;
    this.padScalePerp  += this.padVelPerp  * dt;
    this.padScaleAlong += this.padVelAlong * dt;
    // Map perp/along to x/y based on launch axis.
    if (Math.abs(this.axis.y) > 0.5) {
      this.pad.scale.set(this.padScaleAlong, this.padScalePerp);
    } else {
      this.pad.scale.set(this.padScalePerp, this.padScaleAlong);
    }

    // Ring grow + fade.
    if (this.ringT < 1) {
      this.ringT = Math.min(1, this.ringT + dt * 3.5);
      const t = this.ringT;
      const grow = 1 + t * 0.6;
      const alpha = (1 - t) * 0.55 * this.ringIntensity;
      this.ring.clear();
      this.ring
        .roundRect(
          -this.halfW * grow, -this.halfH * grow,
          this.halfW * 2 * grow, this.halfH * 2 * grow,
          Math.min(Math.min(this.halfW * 2, this.halfH * 2) / 2, 14) * grow,
        )
        .stroke({ color: 0xffffff, width: 3, alpha });
      if (this.ringT >= 1) this.ring.clear();
    }
  }
}
